import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { Button, colors, Icon, MessageBox, Spinner, Table, Typography } from '@dw/helix'

import { css } from '@emotion/core'

import flatten from '../../utils/flatten'
import { ArticlesContext } from '../../contexts'
import TableBadge, { TableBadgeStatus } from './components/TableBadge'
import ManualInputModal, { ModalFormData } from './components/ManualInputModal'
import ArticleSerialSearchModal from './components/ArticleSerialSearchModal'
import ConfirmationModal from './components/ConfirmationModal'
import { makeArticleDescription } from './utils'
import { ScanItem, ScanItemMapping } from './Scan'
import { FeedbackErrorMessage, FeedbackMessageMapping, FeedbackSuccessMessage } from './feedbackMessages'
import usePrevious from '../../hooks/usePrevious'
import { getElementScrollOffset, scrollWindowTop, smoothlyScrollToLocation } from './utils/scroll'

const overviewStyles = css`
  display: flex;
  flex-direction: column;
  align-items: start;
  position: relative;
  height: 100%;
`

const overviewHeaderStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: sticky;
  top: 50px;
  z-index: 1;
  background-color: white;
  width: 100%;
`

const overviewHeaderActionContainerStyles = css`
  margin-left: auto;
  display: flex;
  flex-direction: row;
  > * {
    margin-left: 20px;
  }
`

const overviewFooterStyles = css`
  display: flex;
  flex-direction: row;
  position: sticky;
  z-index: 1;
  bottom: 0;
  padding-top: 12px;
  padding-bottom: 12px;
  width: 100%;
  align-items: center;
  justify-content: end;
  margin-top: auto;
  justify-content: flex-end;
  background: rgb(255, 255, 255);
  background: linear-gradient(
    0deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.8687850140056023) 66%,
    rgba(255, 255, 255, 0) 100%
  );
  > * {
    margin-left: 20px;
  }
`

const feedbackMessageStyles = css`
  margin-bottom: 20px;
  width: 100%;
`

const withEllipsisStyles = css`
  width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const tableRowPlaceholderStyles = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
`

const tableStyles = css`
  width: 100%;
  .active {
    background-color: ${colors.support2b};
  }
`

const scrollContainerStyles = css`
  width: 100%;
  margin-bottom: 20px;
`
const needAttentionStyles = css`
  width: 65%;
  text-align: center;
`

const crossReferenceIconList: { [productGroupCode: string]: string } = {
  WATCH: 'watch',
  STRAP: 'strap',
  'GIFT BOXES': 'gift-wrapping',
  ACCESSORY: 'bracelet',
}

interface ScanOverviewProps {
  isDeleteMode: boolean
  isSubmitDisabled: boolean
  orderNumber: string
  selectedArticleId: string
  scanItemMapping: ScanItemMapping
  isScanItemMappingLoading: boolean
  feedbackMessages: (FeedbackErrorMessage | FeedbackSuccessMessage)[]
  onClearFeedbackMessage: (feedbackMessageToDelete: keyof FeedbackMessageMapping) => void
  onRemoveScanItem: (articleId: string) => void
  onRemoveScanItemSerialNumbers: (articleId: string, serialNumbers: string[]) => void
  onManualInputSubmit: (formData: ModalFormData) => void
  onToggleDeleteMode: () => void
}

export interface Modals {
  MANUAL_INPUT_MODAL: string
  ARTICLE_SERIAL_SEARCH_MODAL: string
  DELETE_SERIAL_NUMBERS_CONFIRMATION_MODAL: string
  [key: string]: string
}

interface ActiveModalInState {
  name: string
  data?: { [key: string]: any }
}

const MODALS: Modals = {
  MANUAL_INPUT_MODAL: 'manualInputModal',
  ARTICLE_SERIAL_SEARCH_MODAL: 'articleSerialSearchModal',
  DELETE_SERIAL_NUMBERS_CONFIRMATION_MODAL: 'deleteSerialNumbersConfirmationModal',
}

interface GetTableBadgeStatusInput {
  overallScanCount: number
  quantity: number
  isManualInput: boolean
}

const getTableBadgeStatus = ({ overallScanCount, quantity, isManualInput }: GetTableBadgeStatusInput) => {
  const isScanQuantityExceeded = overallScanCount > quantity
  const isScanQuantityExpected = overallScanCount === quantity

  if (isManualInput) return TableBadgeStatus.danger
  if (isScanQuantityExceeded) return TableBadgeStatus.danger
  if (isScanQuantityExpected) return TableBadgeStatus.success
  return TableBadgeStatus.normal
}

/**
 * This component requires a parent form as the primary button is of type "submit".
 */
const ScanOverview = (props: ScanOverviewProps) => {
  const {
    isDeleteMode,
    isSubmitDisabled,
    orderNumber,
    selectedArticleId,
    scanItemMapping,
    isScanItemMappingLoading,
    feedbackMessages,
    onClearFeedbackMessage,
    onRemoveScanItem,
    onRemoveScanItemSerialNumbers,
    onManualInputSubmit,
    onToggleDeleteMode,
  } = props
  const scanItems = Object.values(scanItemMapping)

  const { articles, articlesAreLoading } = useContext(ArticlesContext)
  const [activeModalInState, setActiveModalInState] = useState<ActiveModalInState | null>(null)
  const previousSelectedArticleId = usePrevious(selectedArticleId)
  const previousScanItems = usePrevious(scanItems) || []
  const previousFeedbackMessages = usePrevious(feedbackMessages) || []
  const scanItemRefs: HTMLDivElement[] = []

  const isTableLoading = articlesAreLoading || isScanItemMappingLoading
  const scanItemsWithoutManualInputs = scanItems.filter(({ isManualInput }) => !isManualInput)
  const allTraces = flatten(scanItems.map(({ traces }) => traces))
  const allSerialNumbers = flatten(scanItems.map(({ serialNumbers }) => serialNumbers))
  const totalTraceCount = allTraces.length
  const totalSerialNumberCount = allSerialNumbers.length
  const totalScanCount = totalTraceCount + totalSerialNumberCount
  const totalExpectedScanCount = scanItemsWithoutManualInputs.reduce(
    (totalCount, { quantity }) => totalCount + quantity,
    0,
  )

  const scrollToSelectedScanItem = useCallback(() => {
    const selectedArticleIndexInOrder = scanItems.findIndex(item => item.articleId === selectedArticleId)
    if (selectedArticleIndexInOrder === -1) return

    const selectedArticleRowOffset = getElementScrollOffset(scanItemRefs[selectedArticleIndexInOrder])
    smoothlyScrollToLocation(selectedArticleRowOffset)
  }, [scanItems, selectedArticleId, scanItemRefs])

  // useEffect to scroll to selected scan item on selecting article id
  useEffect(() => {
    const isPreviousArticleId = selectedArticleId === previousSelectedArticleId
    if (!isPreviousArticleId) scrollToSelectedScanItem()
  }, [selectedArticleId, previousSelectedArticleId, scrollToSelectedScanItem])

  // useEffect to scroll to selected scan item on adding serial(s)
  useEffect(() => {
    const allPreviousSerialNumbers = flatten(previousScanItems.map(({ serialNumbers }) => serialNumbers))
    if (allSerialNumbers.length > allPreviousSerialNumbers.length) scrollToSelectedScanItem()
  }, [previousScanItems, allSerialNumbers, scrollToSelectedScanItem])

  // useEffect to scroll to top of window on selective feedback message change
  useEffect(() => {
    const isPreviousFeedbackMessages = JSON.stringify(feedbackMessages) === JSON.stringify(previousFeedbackMessages)
    const isGreaterOrEqualFeedbackMessageLength = feedbackMessages.length >= previousFeedbackMessages.length
    if (!isPreviousFeedbackMessages && isGreaterOrEqualFeedbackMessageLength) scrollWindowTop()
  }, [feedbackMessages, previousFeedbackMessages])

  let ActiveModal: ReactElement | null = null

  if (activeModalInState) {
    const activeModalName = activeModalInState.name
    const activeModalArticleId = activeModalInState.data?.articleId
    const activeModalScanItem = scanItemMapping[activeModalArticleId]
    if (activeModalName === MODALS.MANUAL_INPUT_MODAL) {
      ActiveModal = (
        <ManualInputModal
          onClose={() => setActiveModalInState(null)}
          onSubmit={onManualInputSubmit}
          serialNumbers={allSerialNumbers}
          articleId={selectedArticleId}
        />
      )
    }
    if (activeModalName === MODALS.ARTICLE_SERIAL_SEARCH_MODAL) {
      ActiveModal = (
        <ArticleSerialSearchModal
          scanItem={activeModalScanItem}
          onRemoveSerialNumber={(articleId: string, serialNumber: string) =>
            onRemoveScanItemSerialNumbers(articleId, [serialNumber])
          }
          onClose={() => setActiveModalInState(null)}
        />
      )
    }
    if (activeModalName === MODALS.DELETE_SERIAL_NUMBERS_CONFIRMATION_MODAL) {
      ActiveModal = (
        <ConfirmationModal
          title="Remove serials"
          message={`Are you sure you want to delete ${activeModalScanItem.serialNumbers.length} serial numbers from ${activeModalArticleId}?`}
          onConfirm={() => onRemoveScanItemSerialNumbers(activeModalArticleId, activeModalScanItem.serialNumbers)}
          onClose={() => setActiveModalInState(null)}
        />
      )
    }
  }

  const currentRowIndex = scanItems.findIndex(item => item.articleId === selectedArticleId)

  return (
    <div css={overviewStyles}>
      <div css={overviewHeaderStyles}>
        <Typography type="h3">Order No {orderNumber}</Typography>
        <div css={overviewHeaderActionContainerStyles}>
          <Button
            type="button"
            theme="tertiary"
            size="medium"
            onClick={() => setActiveModalInState({ name: MODALS.MANUAL_INPUT_MODAL })}
          >
            Manual Input
          </Button>
          <Button type="button" theme="tertiary" size="medium" onClick={() => onToggleDeleteMode()}>
            {isDeleteMode ? 'End delete mode' : 'Start delete mode '}
          </Button>
          {ActiveModal}
        </div>
      </div>

      {!!feedbackMessages.length &&
        feedbackMessages.map(({ type, name, message }) => (
          <MessageBox
            key={name}
            close={true}
            status={type}
            onClick={() => onClearFeedbackMessage(name)}
            css={feedbackMessageStyles}
          >
            {message}
          </MessageBox>
        ))}

      <div css={scrollContainerStyles}>
        <Table
          css={tableStyles}
          setTrClassname={j => (j === currentRowIndex ? 'active' : '')}
          items={isTableLoading ? [] : scanItems}
          columns={[
            {
              label: '',
              width: '75px',
              render: ({ articleId }) => {
                const article = articles[articleId]
                const iconName = article ? crossReferenceIconList[article?.productGroupCode] : ''
                if (!iconName) return null
                return (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: flex-start;
                    `}
                  >
                    <Icon height="24px" width="19px" name={iconName} />
                  </div>
                )
              },
            },
            {
              label: 'Article No.',
              width: '134px',
              render: ({ articleId }, index) => {
                return (
                  <div
                    ref={ref => {
                      if (index !== undefined && ref) scanItemRefs[index] = ref
                    }}
                  >
                    <Typography type="caption">{articleId}</Typography>
                  </div>
                )
              },
            },
            {
              label: 'Description',
              width: '187px',
              render: ({ articleId }) => {
                const article = articles[articleId]
                const description = article ? makeArticleDescription(article) : ''
                if (!description) return null
                return (
                  <span title={description} css={withEllipsisStyles}>
                    {description}
                  </span>
                )
              },
            },
            {
              label: 'Scanned serials',
              width: '149px',
              render: ({ articleId }) => (
                <Button
                  type="button"
                  theme="tertiary"
                  onClick={() =>
                    setActiveModalInState({
                      name: MODALS.ARTICLE_SERIAL_SEARCH_MODAL,
                      data: { articleId },
                    })
                  }
                >
                  Serial Info
                </Button>
              ),
            },
            {
              label: 'Count',
              width: '120px',
              render: (item: ScanItem) => {
                const { traces, serialNumbers, quantity, isManualInput = false } = item
                const traceCount = traces.length
                const serialNumberCount = serialNumbers.length
                const overallScanCount = traceCount + serialNumberCount

                return (
                  <TableBadge
                    text={`${overallScanCount}${isManualInput ? '' : `/${quantity}`}`}
                    status={getTableBadgeStatus({
                      overallScanCount,
                      quantity,
                      isManualInput,
                    })}
                  />
                )
              },
            },

            {
              label: 'Battery Change',
              width: '140px',
              render: (item: ScanItem) => {
                const { needAttention } = item
                const needAttentionCount = needAttention.length

                return (
                  <div css={needAttentionStyles}>
                <>{needAttentionCount}</>
                </div>
              )},
            },

            {
              label: '',
              width: '176px',
              render: (item: ScanItem) => {
                const { articleId, serialNumbers, isManualInput, Created } = item
                const removeSerialNumbersButtonText = 'Remove Serials'
                if (isManualInput && !Created) {
                  return (
                    <Button type="button" theme="tertiary" onClick={() => onRemoveScanItem(articleId)}>
                      Remove Product
                    </Button>
                  )
                }
                if (serialNumbers.length) {
                  return (
                    <Button
                      type="button"
                      theme="tertiary"
                      onClick={() => {
                        setActiveModalInState({
                          name: MODALS.DELETE_SERIAL_NUMBERS_CONFIRMATION_MODAL,
                          data: { articleId },
                        })
                      }}
                    >
                      {removeSerialNumbersButtonText}
                    </Button>
                  )
                }
                return (
                  <Button type="button" disabled theme="tertiary">
                    {removeSerialNumbersButtonText}
                  </Button>
                )
              },
            },
          ]}
          rowKey={'orderId'}
        />
      </div>
      {isTableLoading && (
        <div css={tableRowPlaceholderStyles}>
          <Spinner />
        </div>
      )}
      {!isTableLoading && !scanItems.length && (
        <div css={tableRowPlaceholderStyles}>
          <Typography type="snippet">No scanning data available.</Typography>
        </div>
      )}
      <div css={overviewFooterStyles}>
        <Typography type="h4">{`${totalScanCount}/${totalExpectedScanCount} Scanned Articles`}</Typography>
        <Button type="submit" theme="primary" size="medium" disabled={isSubmitDisabled}>
          Send Order
        </Button>
      </div>
    </div>
  )
}

export default ScanOverview
